"use client";

/**
 * workflow-graph-viz.tsx — minimal SVG DAG renderer for a WorkflowGraph.
 *
 * First user: the orchestration plan preview card (Phase 2 of spec
 * 2026-04-21-orchestration-mode-design.md). A proper graph library is
 * overkill for ≤10-node fan-outs — this component does breadth-first
 * layering from entry_node, stacks nodes vertically per layer, and draws
 * straight edges with a small arrowhead. Nothing fancy, nothing that
 * needs react-flow or d3.
 *
 * Optional `nodeStatus` lets callers paint live-progress dots once a run
 * is in flight (running / completed / failed / pending).
 *
 * Spec: docs/superpowers/specs/2026-04-22-orchestration-phase-2-synthesis-graphviz-design.md
 */

import { useMemo } from 'react';
import type { WorkflowGraph } from '@/types/workflow';

export type NodeStatus = 'pending' | 'running' | 'completed' | 'failed';

interface LaidOutNode {
  id: string;
  label: string;
  typeHint: string;
  x: number;
  y: number;
}

interface LaidOutEdge {
  from: string;
  to: string;
}

interface Layout {
  nodes: LaidOutNode[];
  edges: LaidOutEdge[];
  width: number;
  height: number;
}

const NODE_W = 140;
const NODE_H = 40;
const H_GAP = 60;
const V_GAP = 16;
const PADDING = 20;

/**
 * Breadth-first layering from entry_node. Nodes at the same BFS depth
 * become a column. Within a column, we preserve first-seen order.
 */
function layout(graph: WorkflowGraph): Layout {
  const nodeIds = Object.keys(graph.nodes ?? {});
  if (nodeIds.length === 0) {
    return { nodes: [], edges: [], width: 0, height: 0 };
  }

  const outgoing = new Map<string, string[]>();
  for (const id of nodeIds) outgoing.set(id, []);
  for (const e of graph.edges ?? []) {
    if (outgoing.has(e.from)) outgoing.get(e.from)!.push(e.to);
  }

  const depth = new Map<string, number>();
  const queue: string[] = [];
  const start = graph.entry_node && graph.nodes[graph.entry_node] ? graph.entry_node : nodeIds[0];
  depth.set(start, 0);
  queue.push(start);
  while (queue.length > 0) {
    const id = queue.shift()!;
    const d = depth.get(id)!;
    for (const next of outgoing.get(id) ?? []) {
      if (!depth.has(next)) {
        depth.set(next, d + 1);
        queue.push(next);
      }
    }
  }
  // Orphan nodes (not reachable from entry) go into a final column.
  const maxBfsDepth = Math.max(0, ...Array.from(depth.values()));
  for (const id of nodeIds) {
    if (!depth.has(id)) depth.set(id, maxBfsDepth + 1);
  }

  // Group ids by depth, keeping insertion order from nodeIds.
  const columns = new Map<number, string[]>();
  for (const id of nodeIds) {
    const d = depth.get(id)!;
    if (!columns.has(d)) columns.set(d, []);
    columns.get(d)!.push(id);
  }

  const maxCol = Math.max(...columns.keys());
  const maxRows = Math.max(...Array.from(columns.values()).map((c) => c.length));

  const laidOut: LaidOutNode[] = [];
  for (const [d, ids] of columns.entries()) {
    ids.forEach((id, i) => {
      const node = graph.nodes[id];
      laidOut.push({
        id,
        label: node?.label || id,
        typeHint: String(node?.type ?? ''),
        x: PADDING + d * (NODE_W + H_GAP),
        y: PADDING + i * (NODE_H + V_GAP),
      });
    });
  }

  return {
    nodes: laidOut,
    edges: (graph.edges ?? []).map((e) => ({ from: e.from, to: e.to })),
    width: PADDING * 2 + (maxCol + 1) * NODE_W + maxCol * H_GAP,
    height: PADDING * 2 + maxRows * NODE_H + Math.max(0, maxRows - 1) * V_GAP,
  };
}

function statusColor(status?: NodeStatus): string {
  switch (status) {
    case 'running':
      return 'var(--primary)';
    case 'completed':
      return 'rgb(16 185 129)'; // emerald-500
    case 'failed':
      return 'rgb(239 68 68)'; // red-500
    default:
      return 'var(--outline-variant)';
  }
}

export function WorkflowGraphViz({
  graph,
  nodeStatus,
}: {
  graph: WorkflowGraph;
  /** Optional map of node id → run status. Missing entries render as pending. */
  nodeStatus?: Record<string, NodeStatus>;
}) {
  const { nodes, edges, width, height } = useMemo(() => layout(graph), [graph]);

  if (nodes.length === 0) {
    return (
      <div className="text-xs text-[var(--on-surface-variant)] italic px-3 py-2">
        (empty graph)
      </div>
    );
  }

  const byId = new Map(nodes.map((n) => [n.id, n]));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto"
      style={{ maxHeight: 280 }}
      role="img"
      aria-label="Workflow graph"
    >
      <defs>
        <marker
          id="arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--outline-variant)" />
        </marker>
      </defs>

      {edges.map((e, i) => {
        const from = byId.get(e.from);
        const to = byId.get(e.to);
        if (!from || !to) return null;
        const x1 = from.x + NODE_W;
        const y1 = from.y + NODE_H / 2;
        const x2 = to.x;
        const y2 = to.y + NODE_H / 2;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--outline-variant)"
            strokeWidth={1.5}
            markerEnd="url(#arrow)"
          />
        );
      })}

      {nodes.map((n) => {
        const status = nodeStatus?.[n.id];
        const color = statusColor(status);
        return (
          <g key={n.id} transform={`translate(${n.x}, ${n.y})`}>
            <rect
              width={NODE_W}
              height={NODE_H}
              rx={8}
              fill="var(--surface-container)"
              stroke={color}
              strokeWidth={status ? 2 : 1}
            />
            <circle cx={12} cy={NODE_H / 2} r={4} fill={color} />
            <text
              x={24}
              y={NODE_H / 2 + 1}
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--on-surface)"
              fontWeight={500}
            >
              {n.label.length > 18 ? n.label.slice(0, 17) + '…' : n.label}
            </text>
            {n.typeHint && (
              <text
                x={24}
                y={NODE_H / 2 + 13}
                dominantBaseline="middle"
                fontSize={9}
                fill="var(--on-surface-variant)"
              >
                {n.typeHint}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
